import React, { useState } from 'react';
import { BellIcon, DevicePhoneMobileIcon, UsersIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  message: string;
  category: string;
  targetAudience: string[];
}

const PushNotifications: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [customTitle, setCustomTitle] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const notificationTemplates: NotificationTemplate[] = [
    {
      id: 'emergency',
      name: 'Emergency Alert',
      title: '🚨 Emergency Alert',
      message: 'Important emergency notification for your area. Please check for updates.',
      category: 'emergency',
      targetAudience: ['all']
    },
    {
      id: 'maintenance',
      name: 'Maintenance Update',
      title: '🔧 Maintenance Update',
      message: 'Scheduled maintenance in your area. We apologize for any inconvenience.',
      category: 'maintenance',
      targetAudience: ['citizens', 'technicians']
    },
    {
      id: 'water',
      name: 'Water Service',
      title: '💧 Water Service Update',
      message: 'Water service status update for your area.',
      category: 'service',
      targetAudience: ['citizens']
    },
    {
      id: 'electricity',
      name: 'Electricity Service',
      title: '⚡ Electricity Service Update',
      message: 'Power service status update for your area.',
      category: 'service',
      targetAudience: ['citizens']
    },
    {
      id: 'general',
      name: 'General Announcement',
      title: '📢 General Announcement',
      message: 'Important information from your municipality.',
      category: 'general',
      targetAudience: ['all']
    }
  ];

  const audienceOptions = [
    { value: 'all', label: 'All Users', count: '2,847' },
    { value: 'citizens', label: 'Citizens Only', count: '2,156' },
    { value: 'technicians', label: 'Technicians Only', count: '691' },
    { value: 'admins', label: 'Administrators Only', count: '23' }
  ];

  const handleTemplateSelect = (templateId: string) => {
    const template = notificationTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setCustomTitle(template.title);
      setCustomMessage(template.message);
      setTargetAudience(template.targetAudience);
    }
  };

  const handleAudienceToggle = (audience: string) => {
    if (audience === 'all') {
      setTargetAudience(['all']);
    } else {
      const newAudience = targetAudience.includes('all') 
        ? [audience]
        : targetAudience.includes(audience)
        ? targetAudience.filter(a => a !== audience)
        : [...targetAudience, audience];
      setTargetAudience(newAudience);
    }
  };

  const handleSendNotification = async () => {
    if (!customTitle.trim() || !customMessage.trim() || targetAudience.length === 0) {
      alert('Please fill in all required fields and select target audience.');
      return;
    }

    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSending(false);
    alert('Notification sent successfully!');
    
    // Reset form
    setSelectedTemplate('');
    setCustomTitle('');
    setCustomMessage('');
    setTargetAudience([]);
  };

  const getEstimatedRecipients = () => {
    if (targetAudience.includes('all')) return '2,847';
    if (targetAudience.includes('citizens') && targetAudience.includes('technicians')) return '2,847';
    if (targetAudience.includes('citizens')) return '2,156';
    if (targetAudience.includes('technicians')) return '691';
    if (targetAudience.includes('admins')) return '23';
    return '0';
  };

  return (
    <div>
      <PageHeader
        title="Push Notifications"
        subtitle="Send real-time notifications to citizens and technicians via mobile apps."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Templates Panel */}
        <div className="lg:col-span-1">
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Templates</h2>
            <div className="space-y-3">
              {notificationTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`w-full text-left p-3 rounded-sm border transition-colors ${
                    selectedTemplate === template.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">{template.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.message.substring(0, 60)}...</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:text-gray-400 rounded">
                    {template.category}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Notification Form */}
        <div className="lg:col-span-2">
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Send Notification</h2>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSendNotification(); }}>
              {/* Title */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notification Title *
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter notification title..."
                  required
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message *
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter notification message..."
                  required
                />
              </div>

              {/* Target Audience */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Target Audience *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {audienceOptions.map((option) => (
                    <label key={option.value} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targetAudience.includes(option.value)}
                        onChange={() => handleAudienceToggle(option.value)}
                        className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{option.label}</span>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{option.count} users</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {customTitle && customMessage && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</h3>
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center gap-2 mb-2">
                      <DevicePhoneMobileIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">Mobile Notification</span>
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">{customTitle}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{customMessage}</p>
                  </div>
                </div>
              )}

              {/* Send Button */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <UsersIcon className="h-4 w-4 inline mr-1" />
                  Estimated recipients: <span className="font-medium">{getEstimatedRecipients()}</span>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  icon={isSending ? undefined : PaperAirplaneIcon}
                  disabled={isSending || !customTitle.trim() || !customMessage.trim() || targetAudience.length === 0}
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    'Send Notification'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Recent Notifications */}
      <div className="mt-8 rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Notifications</h2>
        <EmptyState icon={BellIcon} title="No notifications yet" description="Send your first notification using the form above." />
      </div>
    </div>
  );
};

export default PushNotifications;





